/**
 * Whether AUTO APPLY is offered at all (VE-24948 / U5).
 *
 * Its own module for the same two reasons as `auditConfig`: it is the one
 * switch a reader goes looking for, and the behaviour it gates has tests that
 * must keep describing what happens when it comes back on.
 *
 * ── Why it is OFF ──────────────────────────────────────────────────────────
 *
 * It competes with the upload picker, and a user cannot see that it does.
 * There are two ways for a lexicon to reach a document without anyone choosing
 * it in the editor:
 *
 *   - the UPLOAD PICKER, which records the selection against each new TDO and
 *     evaluates it at first open (BR-49 / BR-50). It carries the last choice
 *     forward, like every other checkbox in that dialog, so a selection made
 *     once keeps applying to later uploads until it is changed;
 *   - AUTO APPLY, a library flag that applies a lexicon on first open of any
 *     document that has never been saved, whatever the upload said.
 *
 * Both are "applies itself", they are set in different places, and neither
 * names the other. An operator who ticked a lexicon at upload and later
 * wondered why a DIFFERENT one was applied has nothing on screen to explain
 * it. The upload picker wins because it is per-batch, visible at the moment of
 * choosing, and already the documented entry point (`U5-FR14`).
 *
 * `autoApplyLexiconsOnOpen` already skips anything the upload selector
 * attached, which is the seam that let the two coexist — but avoiding a double
 * apply is not the same as being explicable.
 *
 * ── What this switch does ──────────────────────────────────────────────────
 *
 * Hides the settings table's Auto Apply column AND short-circuits the saga
 * that acts on the flag. Both, deliberately: a hidden setting whose behaviour
 * still runs is worse than either one alone, because the only way to observe
 * it is a document that changed for no visible reason.
 *
 * The FIELD stays — written by `setLexiconFlags`, carried by the schema, read
 * by `selectApplicableLexicons` — so a value set before this switch existed is
 * preserved rather than quietly cleared, and turning the feature back on is a
 * one-line change rather than a migration.
 *
 * ── Before turning it on ───────────────────────────────────────────────────
 *
 * Decide which of the two wins and say so on screen. U5-FR9 ("a lexicon may be
 * designated to run automatically on upload") was RETIRED on 2026-08-03, so
 * the requirement this flag implements no longer exists — see
 * `plans/lexicons-follow-ups.md`. Either that is amended, or the wiring comes
 * out for good.
 */
export const LEXICON_AUTO_APPLY_ENABLED = false;
